import { Eyebrow } from './eyebrow';

/**
 * A labelled input in the concept's idiom.
 *
 * The label is the mono eyebrow the concept uses everywhere else, so a form
 * reads as the same product as a card. Errors sit under the field rather than
 * in a toast: a toast that says "Please fill in all required fields" makes the
 * reader hunt for which one.
 */
export const Field: React.FC<{
  id: string;
  label: string;
  type?: 'text' | 'email' | 'number' | 'date';
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  min?: number;
  max?: number;
  error?: string;
  hint?: string;
  className?: string;
}> = ({
  id,
  label,
  type = 'text',
  value,
  onChange,
  placeholder,
  required,
  min,
  max,
  error,
  hint,
  className = '',
}) => (
  <div className={`flex flex-col gap-1.5 ${className}`}>
    <label htmlFor={id}>
      <Eyebrow>
        {label}
        {required && <span className="ml-1 text-[hsl(var(--projected))]">*</span>}
      </Eyebrow>
    </label>
    <input
      id={id}
      type={type}
      value={value}
      placeholder={placeholder}
      min={min}
      max={max}
      aria-invalid={error ? true : undefined}
      aria-describedby={error ? `${id}-error` : hint ? `${id}-hint` : undefined}
      onChange={(e) => onChange(e.target.value)}
      className={`tabular w-full rounded-lg border bg-card px-3 py-2 text-sm outline-none transition-colors placeholder:text-muted-foreground ${
        error ? 'border-destructive' : 'border-border focus:border-primary'
      }`}
    />
    {error ? (
      <p id={`${id}-error`} className="text-[11px] text-destructive">
        {error}
      </p>
    ) : hint ? (
      <p id={`${id}-hint`} className="text-[11px] text-muted-foreground">
        {hint}
      </p>
    ) : null}
  </div>
);

/** Two mutually exclusive choices, as chips rather than radio dots. */
export const ChoiceField: React.FC<{
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
  required?: boolean;
}> = ({ label, value, options, onChange, required }) => (
  <div className="flex flex-col gap-1.5">
    <Eyebrow>
      {label}
      {required && <span className="ml-1 text-[hsl(var(--projected))]">*</span>}
    </Eyebrow>
    <div role="radiogroup" aria-label={label} className="flex gap-2">
      {options.map((o) => {
        const on = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            id={o.value}
            aria-checked={on}
            onClick={() => onChange(o.value)}
            className={`rounded-lg border px-3.5 py-2 text-xs font-medium transition-colors ${
              on
                ? 'border-primary bg-accent text-accent-foreground'
                : 'border-border bg-card text-foreground-soft hover:bg-shell'
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  </div>
);
